import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  Calendar as CalendarIcon,
  Download,
  FileText,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatCNPJ } from '@/lib/utils/formatters';
import { useToast } from '@/hooks/use-toast';
import { useGerarLinkTemporario } from '@/services/General';
import {
  listarAgendamentosPeriodoApi,
  useGerarRelatorioPDFLote,
  type AgendamentoApi,
} from '@/services/Relatorios';

interface ModalExportarPorDataProps {
  aberto: boolean;
  onFechar: () => void;
  cnpjContribuinte: string | null;
}

const ITENS_POR_PAGINA = 5;

export function ModalExportarPorData({
  aberto,
  onFechar,
  cnpjContribuinte,
}: ModalExportarPorDataProps) {
  const [dataSelecionada, setDataSelecionada] = useState<Date | undefined>(undefined);
  const [agendamentosSelecionados, setAgendamentosSelecionados] = useState<Set<string>>(new Set());
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [calendarioAberto, setCalendarioAberto] = useState(false);
  const seletorDataRef = useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();

  const gerarLinkTemporario = useGerarLinkTemporario();
  const gerarRelatorioPDFLote = useGerarRelatorioPDFLote();

  const dataFormatadaApi = dataSelecionada ? format(dataSelecionada, 'yyyy-MM-dd') : '';
  const cnpjConsulta = cnpjContribuinte?.trim() ?? '';
  const queryHabilitada = aberto && Boolean(dataFormatadaApi) && Boolean(cnpjConsulta);

  const { data: agendamentos, isLoading: isLoadingAgendamentos } = useQuery({
    queryKey: ['relatorios', 'agendamentos-periodo-modal', cnpjConsulta, dataFormatadaApi],
    queryFn: () =>
      listarAgendamentosPeriodoApi({
        cnpjContribuinte: cnpjConsulta,
        data: dataFormatadaApi,
      }),
    enabled: queryHabilitada,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (!calendarioAberto) return;

    const handlePointerDownFora = (event: MouseEvent) => {
      if (!seletorDataRef.current?.contains(event.target as Node)) {
        setCalendarioAberto(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDownFora);

    return () => {
      document.removeEventListener('mousedown', handlePointerDownFora);
    };
  }, [calendarioAberto]);

  const isExportando = gerarRelatorioPDFLote.isPending || gerarLinkTemporario.isPending;

  const agendamentosDisponiveis = useMemo(() => {
    const itens = agendamentos ?? [];
    const unicos = new Map<number, AgendamentoApi>();

    for (const item of itens) {
      if (item.Agendamento_Status === 'Relatório Disponivel') {
        unicos.set(item.id, item);
      }
    }

    return Array.from(unicos.values());
  }, [agendamentos]);

  const totalPaginas = Math.ceil(agendamentosDisponiveis.length / ITENS_POR_PAGINA);

  const agendamentosPaginados = useMemo(() => {
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    return agendamentosDisponiveis.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [agendamentosDisponiveis, paginaAtual]);

  const handleSelecionarAgendamento = (id: string, checked: boolean) => {
    const novo = new Set(agendamentosSelecionados);
    if (checked) novo.add(id);
    else novo.delete(id);
    setAgendamentosSelecionados(novo);
  };

  const handleSelecionarTodos = (checked: boolean) => {
    if (checked) {
      setAgendamentosSelecionados(
        new Set(agendamentosDisponiveis.map(agendamento => agendamento.id.toString()))
      );
    } else {
      setAgendamentosSelecionados(new Set());
    }
  };

  const handleExportar = async () => {
    if (agendamentosSelecionados.size === 0) {
      toast({
        variant: 'destructive',
        title: 'Nenhum agendamento selecionado',
        description: 'Selecione pelo menos um agendamento para exportar.',
      });
      return;
    }

    try {
      const caminhoArquivo = await gerarRelatorioPDFLote.mutateAsync({
        agendamento_ids: Array.from(agendamentosSelecionados).map(Number),
      });

      if (!caminhoArquivo) {
        throw new Error('Caminho do arquivo inválido.');
      }

      const linkResponse = await gerarLinkTemporario.mutateAsync({
        Funcao: 'GET',
        Bucket: 'imposto-cache',
        Key: caminhoArquivo,
      });

      if (!linkResponse.status?.resultado) {
        throw new Error(
          linkResponse.status?.mensagem || 'Não foi possível gerar o link de download.'
        );
      }

      const urlDownload =
        typeof linkResponse.data === 'string' ? linkResponse.data : linkResponse.data?.url;

      if (!urlDownload) {
        throw new Error('Link de download não retornado pela API.');
      }

      const a = document.createElement('a');
      a.href = urlDownload;
      a.target = '_blank';
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();

      toast({
        title: 'Exportação concluída',
        description: `${agendamentosSelecionados.size} relatório(s) exportado(s) com sucesso.`,
      });

      onFechar();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Não foi possível exportar os relatórios.';
      toast({
        variant: 'destructive',
        title: 'Erro ao exportar',
        description: message,
      });
    }
  };

  if (!aberto) return null;

  const content = (
    <>
      <div className="fixed inset-0 bg-black/50 z-100" onClick={onFechar} />
      <div className="fixed inset-0 flex items-end sm:items-center justify-center z-100 p-0 sm:p-4">
        <div
          className="bg-card border-t sm:border border-border rounded-t-2xl sm:rounded-xl w-full sm:max-w-3xl h-[96vh] sm:h-[92vh] sm:max-h-[92vh] flex flex-col shadow-xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border shrink-0">
            <div>
              <h2 className="text-base sm:text-lg font-semibold">Exportar Relatórios por Data</h2>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                Selecione uma data para buscar automaticamente os relatórios da empresa escolhida no
                header global
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onFechar} className="rounded-full h-9 w-9">
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Data de Processamento</label>
              <div ref={seletorDataRef} className="relative">
                <Button
                  variant="outline"
                  onClick={() => setCalendarioAberto(abertoAtual => !abertoAtual)}
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !dataSelecionada && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dataSelecionada
                    ? dataSelecionada.toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })
                    : 'Selecione uma data'}
                </Button>

                {calendarioAberto && (
                  <div className="absolute left-0 top-full z-20 mt-2 rounded-md border border-border bg-popover p-2 shadow-md">
                    <Calendar
                      mode="single"
                      selected={dataSelecionada}
                      onSelect={data => {
                        setDataSelecionada(data);
                        setAgendamentosSelecionados(new Set());
                        setPaginaAtual(1);
                        setCalendarioAberto(false);
                      }}
                      locale={ptBR}
                      initialFocus
                    />
                  </div>
                )}
              </div>
            </div>

            {cnpjConsulta && (
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{formatCNPJ(cnpjConsulta)}</Badge>
              </div>
            )}

            {dataSelecionada && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {agendamentosDisponiveis.length} relatório
                    {agendamentosDisponiveis.length !== 1 ? 's' : ''} disponível
                    {agendamentosDisponiveis.length !== 1 ? 'is' : ''}
                  </span>
                  {agendamentosDisponiveis.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={agendamentosSelecionados.size === agendamentosDisponiveis.length}
                        onCheckedChange={handleSelecionarTodos}
                        id="selecionar-todos"
                      />
                      <label htmlFor="selecionar-todos" className="text-xs cursor-pointer">
                        Selecionar todos
                      </label>
                    </div>
                  )}
                </div>

                {isLoadingAgendamentos ? (
                  <div className="flex flex-col items-center justify-center py-12 space-y-3">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">
                      Carregando relatórios do período...
                    </p>
                  </div>
                ) : (
                  <>
                    {agendamentosPaginados.map(agendamento => (
                      <div
                        key={agendamento.id}
                        className="flex items-center gap-3 bg-muted/30 border border-border rounded-lg p-3"
                      >
                        <Checkbox
                          checked={agendamentosSelecionados.has(agendamento.id.toString())}
                          onCheckedChange={checked =>
                            handleSelecionarAgendamento(agendamento.id.toString(), !!checked)
                          }
                        />
                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {agendamento.Nome_Relatorio}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatCNPJ(agendamento.CNPJ_Contribuinte)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(agendamento.DataHora_Agendamento).toLocaleString('pt-BR')} ·
                            ID: {agendamento.id}
                          </p>
                        </div>
                        <Badge variant="default" className="shrink-0">
                          <Check className="h-3 w-3 mr-1" />
                          Disponível
                        </Badge>
                      </div>
                    ))}

                    {agendamentosDisponiveis.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-12 space-y-3">
                        <FileText className="h-6 w-6 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          Nenhum relatório disponível para a data selecionada.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {totalPaginas > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-xs text-muted-foreground">
                      Página {paginaAtual} de {totalPaginas}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setPaginaAtual(pagina => Math.max(1, pagina - 1))}
                        disabled={paginaAtual === 1}
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setPaginaAtual(pagina => Math.min(totalPaginas, pagina + 1))}
                        disabled={paginaAtual === totalPaginas}
                      >
                        <ChevronRight className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 px-4 sm:px-6 py-4 border-t border-border shrink-0">
            <Button variant="outline" onClick={onFechar} className="w-full sm:w-auto">
              Cancelar
            </Button>
            <Button
              onClick={handleExportar}
              disabled={agendamentosSelecionados.size === 0 || isExportando}
              className="w-full sm:w-auto"
            >
              {isExportando ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Exportar{' '}
              {agendamentosSelecionados.size > 0 ? `${agendamentosSelecionados.size} ` : ''}
              PDFs
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
}
